import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { SEOHead } from '@/components/SEOHead'
import { HeroSection } from '@/components/sections/HeroSection'
import { LocalizedLink } from '@/components/LocalizedLink'
import { ProductCard } from '@/components/products/ProductCard'
import { getSectorBySlug } from '@/data/sectors'
import { products, productCategories } from '@/data/products'
import { NotFoundPage } from './NotFoundPage'
import type { SupportedLanguage } from '@/i18n'

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true } as const,
}

export function SectorDetailPage() {
  const { sectorSlug } = useParams<{ sectorSlug: string }>()
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'it') as SupportedLanguage

  const sector = sectorSlug ? getSectorBySlug(sectorSlug) : undefined
  if (!sector) return <NotFoundPage />

  const recommendedProducts = sector.recommendedProductIds
    .map((id) => products.find((p) => p.id === id))
    .filter((p): p is (typeof products)[number] => p !== undefined)

  const contactPath = lang === 'en' ? '/contacts' : '/contatti'

  // Derive i18n prefix from sector.nameKey: "sectors.police.name" → "sectors.police"
  const sectorPrefix = sector.nameKey.replace('.name', '')

  const challenges = [1, 2, 3, 4] as const

  return (
    <>
      <SEOHead titleKey={sector.nameKey} descriptionKey={sector.descriptionKey} />

      <HeroSection
        title={t(sector.nameKey)}
        subtitle={t(sector.descriptionKey)}
        backgroundImage={sector.heroImage}
      />

      {/* 1. Sector Identity — bg-primary dark */}
      <section className="relative bg-primary section-padding overflow-hidden">
        {/* Decorative blur circle */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

        <div className="container-main relative z-10 max-w-4xl mx-auto text-center">
          <motion.h2
            {...fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            {t(`${sectorPrefix}.statement`)}
          </motion.h2>

          {/* Accent bar */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-20 h-1 bg-accent mx-auto mb-8 origin-center"
          />

          <motion.p
            {...fadeInUp}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-white/70 text-lg leading-relaxed max-w-3xl mx-auto"
          >
            {t(`${sectorPrefix}.statementDesc`)}
          </motion.p>
        </div>
      </section>

      {/* 2. Challenges — light premium */}
      <section className="relative bg-white section-padding overflow-hidden">
        {/* Decorative */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-accent/[0.03] rounded-full blur-3xl pointer-events-none" />

        <div className="container-main relative z-10">
          <div className="text-center mb-14">
            <motion.span
              {...fadeInUp}
              transition={{ duration: 0.5 }}
              className="text-accent text-sm font-semibold uppercase tracking-wider"
            >
              {t('sectors.challengesTitle')}
            </motion.span>
            <motion.h2
              {...fadeInUp}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-2xl md:text-3xl font-bold text-text-heading mt-3"
            >
              {t(`${sectorPrefix}.statement`)}
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {challenges.map((n, index) => (
              <motion.div
                key={n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="group relative rounded-2xl bg-surface-light border border-surface-border p-7 overflow-hidden hover:border-accent/30 hover:shadow-xl transition-all duration-300"
              >
                {/* Accent left bar */}
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent/20 group-hover:bg-accent transition-colors duration-300" />

                {/* Ghost number */}
                <span className="absolute top-5 right-6 text-[3.5rem] font-black text-text-heading/[0.04] leading-none pointer-events-none select-none">
                  {String(n).padStart(2, '0')}
                </span>

                {/* Number badge */}
                <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                  <span className="text-accent font-bold text-sm">{String(n).padStart(2, '0')}</span>
                </div>

                <h3 className="text-lg font-semibold text-text-heading mb-2.5 relative z-10">
                  {t(`${sectorPrefix}.challenge${n}Title`)}
                </h3>
                <p className="text-text-muted leading-relaxed text-sm relative z-10">
                  {t(`${sectorPrefix}.challenge${n}Desc`)}
                </p>

                {/* Bottom accent bar on hover */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Why Selea — bg-surface-light */}
      <section className="bg-surface-light section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <motion.h2
              {...fadeInUp}
              transition={{ duration: 0.5 }}
              className="text-2xl md:text-3xl font-bold text-text-heading mb-4"
            >
              {t(`${sectorPrefix}.whyTitle`)}
            </motion.h2>
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="w-16 h-1 bg-accent mx-auto mb-6 origin-center"
            />
            <motion.p
              {...fadeInUp}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="text-text-body text-lg max-w-2xl mx-auto"
            >
              {t(`${sectorPrefix}.whySubtitle`)}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {challenges.map((n, index) => (
              <motion.div
                key={n}
                {...fadeInUp}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-1.5 w-2.5 h-2.5 rounded-full bg-accent shrink-0" />
                  <p className="text-text-heading leading-relaxed text-sm">
                    {t(`${sectorPrefix}.why${n}`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Recommended products */}
      <section className="bg-white section-padding">
        <div className="container-main">
          <motion.h3
            {...fadeInUp}
            transition={{ duration: 0.5 }}
            className="text-xl md:text-2xl font-bold text-text-heading text-center mb-10"
          >
            {t('sectors.recommendedProducts')}
          </motion.h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendedProducts.map((product, index) => {
              const category = productCategories.find((c) => c.id === product.category)
              if (!category) return null

              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  category={category}
                  lang={lang}
                  index={index}
                  showCategory
                />
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner — Split cinematic */}
      <section className="relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[400px]">
          {/* Left — image */}
          <div className="relative min-h-[250px] lg:min-h-0">
            <img
              src={sector.heroImage}
              alt={t(sector.nameKey)}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            {/* Fade edge desktop */}
            <div className="hidden lg:block absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-primary to-transparent" />
            {/* Fade bottom mobile */}
            <div className="lg:hidden absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-primary to-transparent" />
          </div>

          {/* Right — content */}
          <div className="relative bg-primary flex items-center">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 p-10 lg:p-16 max-w-lg">
              <motion.h2
                {...fadeInUp}
                transition={{ duration: 0.5 }}
                className="text-3xl md:text-4xl font-bold text-white mb-4"
              >
                {t('products.detail.ctaTitle')}
              </motion.h2>
              <motion.p
                {...fadeInUp}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-white/70 leading-relaxed mb-8"
              >
                {t('products.detail.ctaDesc')}
              </motion.p>
              <motion.div
                {...fadeInUp}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <LocalizedLink
                  to={contactPath}
                  className="btn-primary"
                >
                  {t('buttons.contactUs')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </LocalizedLink>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
